import React, { useState, useEffect } from 'react';
import { useAuth, API } from '../App';
import { useT } from '../i18n';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import ConfirmDialog from './ConfirmDialog';
import XpnsrEmpty from './XpnsrEmpty';
import { Switch } from './ui/switch';
import { Separator } from './ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Plus,
  Edit,
  Trash2,
  Pause,
  Zap,
  TrendingDown,
  DollarSign,
  Clock,
  RefreshCw
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import XpnsrPageHeader from './XpnsrPageHeader';
import AutomationHistory from './AutomationHistory';

export default function Automation() {
  const { user } = useAuth();
  const { t } = useT();
  const [rules, setRules] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rules');

  // Dialog states
  const [createRuleDialogOpen, setCreateRuleDialogOpen] = useState(false);
  const [editRuleDialogOpen, setEditRuleDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  // Form state
  const [ruleForm, setRuleForm] = useState({
    name: '',
    description: '',
    rule_type: 'campaign_pause',
    trigger_conditions: {},
    actions: [],
    cooldown_minutes: 60,
    max_executions_per_day: 10
  });

  useEffect(() => {
    Promise.all([fetchAutomationRules(), fetchCampaigns()]);
  }, []);

  const fetchAutomationRules = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/automation/rules`);
      setRules(response.data);
    } catch (error) {
      console.error('Error fetching automation rules:', error);
      toast.error(t('Failed to load automation rules'));
    } finally {
      setLoading(false);
    }
  };

  const fetchCampaigns = async () => {
    try {
      const response = await axios.get(`${API}/campaigns`);
      setCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    }
  };

  const handleCreateRule = async () => {
    try {
      const response = await axios.post(`${API}/automation/rules`, ruleForm);
      setRules([...rules, response.data]);
      setCreateRuleDialogOpen(false);
      resetRuleForm();
      toast.success(t('Automation rule created'));
    } catch (error) {
      console.error('Error creating automation rule:', error);
      toast.error(t('Failed to create automation rule'));
    }
  };

  const handleUpdateRule = async () => {
    if (!editingRule) return;

    try {
      const response = await axios.put(`${API}/automation/rules/${editingRule.id}`, ruleForm);
      setRules(rules.map(rule => rule.id === editingRule.id ? response.data : rule));
      setEditRuleDialogOpen(false);
      setEditingRule(null);
      resetRuleForm();
      toast.success(t('Automation rule updated'));
    } catch (error) {
      console.error('Error updating automation rule:', error);
      toast.error(t('Failed to update automation rule'));
    }
  };

  const [deleteTarget, setDeleteTarget] = useState(null);

  const handleDeleteRule = async (ruleId) => {
    try {
      await axios.delete(`${API}/automation/rules/${ruleId}`);
      setRules(rules.filter(rule => rule.id !== ruleId));
      setDeleteTarget(null);
      toast.success(t('Automation rule deleted'));
    } catch (error) {
      console.error('Error deleting automation rule:', error);
      toast.error(t('Failed to delete automation rule'));
    }
  };

  const handleToggleRule = async (ruleId, isActive) => {
    try {
      await axios.put(`${API}/automation/rules/${ruleId}`, { is_active: !isActive });
      setRules(rules.map(rule =>
        rule.id === ruleId ? { ...rule, is_active: !isActive } : rule
      ));
      toast.success(!isActive ? t('Rule activated') : t('Rule deactivated'));
    } catch (error) {
      console.error('Error toggling rule:', error);
      toast.error(t('Failed to change rule status'));
    }
  };

  const resetRuleForm = () => {
    setRuleForm({
      name: '',
      description: '',
      rule_type: 'campaign_pause',
      trigger_conditions: {},
      actions: [],
      cooldown_minutes: 60,
      max_executions_per_day: 10
    });
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      name: rule.name,
      description: rule.description || '',
      rule_type: rule.rule_type,
      trigger_conditions: rule.trigger_conditions || {},
      actions: rule.actions || [],
      cooldown_minutes: rule.cooldown_minutes,
      max_executions_per_day: rule.max_executions_per_day
    });
    setEditRuleDialogOpen(true);
  };

  const getRuleTypeIcon = (type) => {
    switch (type) {
      case 'campaign_pause': return <Pause className="w-5 h-5 text-destructive" />;
      case 'budget_alert': return <DollarSign className="w-5 h-5 text-yellow-500" />;
      case 'performance_alert': return <TrendingDown className="w-5 h-5 text-orange-500" />;
      case 'offer_rotation': return <RefreshCw className="w-5 h-5 text-primary" />;
      default: return <Zap className="w-5 h-5 text-primary" />;
    }
  };

  const getRuleTypeLabel = (type) => {
    switch (type) {
      case 'campaign_pause': return t('Pause campaign');
      case 'budget_alert': return t('Budget alert');
      case 'performance_alert': return t('Performance alert');
      case 'roi_alert': return t('ROI alert');
      case 'offer_rotation': return t('Offer rotation on daily cap');
      default: return type;
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        <div className="skeleton h-8 w-48 mb-6"></div>
        <div className="skeleton h-96 w-full"></div>
      </div>
    );
  }

  // Shared rule form (name / type / description / trigger conditions /
  // execution settings) rendered by BOTH the create and edit dialogs, so the
  // edit dialog can no longer silently drop the trigger + execution fields.
  const renderRuleForm = () => (
    <>
          {/* Basic Settings */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>{t('Rule name')}</Label>
              <Input
                value={ruleForm.name}
                onChange={(e) => setRuleForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('Rule name')}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('Rule type')}</Label>
              <Select
                value={ruleForm.rule_type}
                onValueChange={(value) => setRuleForm(prev => ({ ...prev, rule_type: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="campaign_pause">{t('Pause campaign')}</SelectItem>
                  <SelectItem value="budget_alert">{t('Budget alert')}</SelectItem>
                  <SelectItem value="performance_alert">{t('Performance alert')}</SelectItem>
                  <SelectItem value="roi_alert">{t('ROI alert')}</SelectItem>
                  <SelectItem value="offer_rotation">{t('Offer rotation on daily cap')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>{t('Description')}</Label>
            <Textarea
              value={ruleForm.description}
              onChange={(e) => setRuleForm(prev => ({ ...prev, description: e.target.value }))}
              placeholder={t('Rule description')}
            />
          </div>

          <Separator />

          {/* Trigger Conditions */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">{t('Trigger conditions')}</Label>

            {ruleForm.rule_type === 'campaign_pause' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('Maximum cost per click')}</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={ruleForm.trigger_conditions.max_cpc || ''}
                    onChange={(e) => setRuleForm(prev => ({
                      ...prev,
                      trigger_conditions: { ...prev.trigger_conditions, max_cpc: parseFloat(e.target.value) || 0 }
                    }))}
                    placeholder="1.50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>{t('Minimum CR (%)')}</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={ruleForm.trigger_conditions.min_conversion_rate || ''}
                    onChange={(e) => setRuleForm(prev => ({
                      ...prev,
                      trigger_conditions: { ...prev.trigger_conditions, min_conversion_rate: parseFloat(e.target.value) || 0 }
                    }))}
                    placeholder="2.5"
                  />
                </div>
              </div>
            )}

            {ruleForm.rule_type === 'roi_alert' && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>{t('Minimum ROI (%)')}</Label>
                  <Input
                    type="number"
                    step="1"
                    value={ruleForm.trigger_conditions.min_roi ?? ''}
                    onChange={(e) => setRuleForm(prev => ({
                      ...prev,
                      trigger_conditions: { ...prev.trigger_conditions, min_roi: parseFloat(e.target.value) || 0 }
                    }))}
                    placeholder="0"
                  />
                  <p className="text-xs text-muted-foreground">{t('Alert if ROI drops below this %')}</p>
                </div>
                <div className="space-y-2">
                  <Label>{t('Action on trigger')}</Label>
                  <Select
                    value={(ruleForm.actions?.[0]?.type) || 'notify'}
                    onValueChange={(v) => setRuleForm(prev => ({
                      ...prev,
                      actions: [{ type: v }]
                    }))}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="notify">{t('Notify only')}</SelectItem>
                      <SelectItem value="pause_campaign">{t('Pause campaign')}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {ruleForm.rule_type === 'budget_alert' && (
              <div className="space-y-2">
                <Label>{t('Budget percentage for alert')}</Label>
                <Input
                  type="number"
                  value={ruleForm.trigger_conditions.budget_threshold || ''}
                  onChange={(e) => setRuleForm(prev => ({
                    ...prev,
                    trigger_conditions: { ...prev.trigger_conditions, budget_threshold: parseInt(e.target.value) || 0 }
                  }))}
                  placeholder="80"
                />
              </div>
            )}

            {ruleForm.rule_type === 'offer_rotation' && (
              <div className="rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-foreground">
                {t('No threshold needed. This rule fires when any offer used by the campaign reaches its daily cap and notifies you that traffic is rotating to the fallback / remaining offers. Set the cap and fallback offer on each offer.')}
              </div>
            )}
          </div>

          <Separator />

          {/* Settings */}
          <div className="space-y-4">
            <Label className="text-base font-semibold">{t('Execution settings')}</Label>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('Interval between executions (min)')}</Label>
                <Input
                  type="number"
                  value={ruleForm.cooldown_minutes}
                  onChange={(e) => setRuleForm(prev => ({ ...prev, cooldown_minutes: parseInt(e.target.value) || 0 }))}
                  min="5"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('Maximum executions per day')}</Label>
                <Input
                  type="number"
                  value={ruleForm.max_executions_per_day}
                  onChange={(e) => setRuleForm(prev => ({ ...prev, max_executions_per_day: parseInt(e.target.value) || 0 }))}
                  min="1"
                />
              </div>
            </div>
          </div>
    </>
  );

  return (
    <div className="p-8 space-y-8">
      <XpnsrPageHeader
        kicker={t('Workflows')}
        title={t('Automation')}
        subtitle={`${t('Set up rules and triggered actions for your campaigns.')} ${t('Rules count days in UTC: "today" starts at 00:00 UTC and may differ from the reports.')}`}
      />

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="flex w-full flex-wrap h-auto justify-start">
          <TabsTrigger value="rules" className="flex items-center space-x-2">
            <Zap className="w-4 h-4" />
            <span>{t('Rules')}</span>
          </TabsTrigger>
          <TabsTrigger value="history" className="flex items-center space-x-2">
            <Clock className="w-4 h-4" />
            <span>{t('History')}</span>
          </TabsTrigger>
        </TabsList>

        {/* Automation Rules Tab */}
        <TabsContent value="rules" className="space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold">{t('Automation rules')}</h2>
            <Dialog open={createRuleDialogOpen} onOpenChange={(open) => { setCreateRuleDialogOpen(open); if (!open) resetRuleForm(); }}>
              <DialogTrigger asChild>
                <Button onClick={resetRuleForm}>
                  <Plus className="w-4 h-4 mr-2" />
                  {t('Create rule')}
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{t('Create automation rule')}</DialogTitle>
                  <DialogDescription>
                    {t('Configure automatic actions based on conditions')}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-6">
                  {renderRuleForm()}

                  <div className="flex justify-end space-x-2 pt-4">
                    <Button variant="outline" onClick={() => setCreateRuleDialogOpen(false)}>
                      {t('Cancel')}
                    </Button>
                    <Button onClick={handleCreateRule}>
                      {t('Create')}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid gap-4">
            {rules.length === 0 ? (
              <Card>
                <CardContent className="py-12">
                  <XpnsrEmpty
                    icon={Zap}
                    title={t('No automation rules')}
                    subtitle={t('Create rules to automatically pause, resume, or adjust campaigns based on performance thresholds.')}
                  />
                </CardContent>
              </Card>
            ) : (
              rules.map((rule) => (
                <Card key={rule.id} className="border">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        {getRuleTypeIcon(rule.rule_type)}
                        <div>
                          <CardTitle className="text-lg">{rule.name}</CardTitle>
                          <CardDescription>{getRuleTypeLabel(rule.rule_type)}</CardDescription>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Badge variant={rule.is_active ? "default" : "secondary"}>
                          {rule.is_active ? t('Active') : t('Paused')}
                        </Badge>
                        <Switch
                          checked={rule.is_active}
                          onCheckedChange={() => handleToggleRule(rule.id, rule.is_active)}
                        />
                        <Button variant="ghost" size="sm" aria-label={t('Edit automation rule')} title={t('Edit automation rule')} onClick={() => openEditRule(rule)}>
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button variant="ghost" size="sm" aria-label={t('Delete automation rule')} title={t('Delete automation rule')} onClick={() => setDeleteTarget(rule)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {rule.description && (
                      <p className="text-sm text-muted-foreground mb-4">{rule.description}</p>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('Executions')}</Label>
                        <p className="font-semibold">{rule.total_executions || 0}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('Interval')}</Label>
                        <p className="font-semibold">{t('{n} min', { n: rule.cooldown_minutes })}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-muted-foreground">{t('Daily limit')}</Label>
                        <p className="font-semibold">{rule.max_executions_per_day}</p>
                      </div>
                    </div>

                    {rule.last_execution && (
                      <div className="mt-4 pt-4 border-t border-border">
                        <Label className="text-xs text-muted-foreground">{t('Last execution')}</Label>
                        <p className="text-sm">{new Date(rule.last_execution).toLocaleString('ru-RU')}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* History Tab: GET /api/automation/history */}
        <TabsContent value="history" className="space-y-6">
          <AutomationHistory
            campaigns={campaigns}
            rules={rules}
            ruleTypeLabel={getRuleTypeLabel}
            active={activeTab === 'history'}
          />
        </TabsContent>
      </Tabs>

      {/* Edit Rule Dialog */}
      <Dialog open={editRuleDialogOpen} onOpenChange={(open) => { setEditRuleDialogOpen(open); if (!open) { setEditingRule(null); resetRuleForm(); } }}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t('Edit automation rule')}</DialogTitle>
            <DialogDescription>
              {t('Change automation rule settings')}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {renderRuleForm()}

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setEditRuleDialogOpen(false)}>
                {t('Cancel')}
              </Button>
              <Button onClick={handleUpdateRule}>
                {t('Save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}
        title={t('Delete automation rule')}
        description={t('This will permanently delete rule "{name}".').replace('{name}', deleteTarget?.name || '')}
        confirmLabel={t('Delete')}
        onConfirm={() => handleDeleteRule(deleteTarget?.id)}
      />
    </div>
  );
}
